import {
  backButton,
  init,
  isTMA,
  miniApp,
  mockTelegramEnv,
  retrieveLaunchParams,
  retrieveRawInitData,
  themeParams,
  viewport,
} from '@telegram-apps/sdk-react';
import { START_PARAM_RE } from '@debtbot/shared';

export type TelegramEnv = {
  rawInitData: string;
  startParam: string | null;
  platform: string;
  version: string;
  /** language_code пользователя из initData (до загрузки /me). */
  languageCode: string | null;
};

/**
 * Разработка в обычном браузере: VITE_DEV_INIT_DATA (подписанный initData из
 * `pnpm --filter @debtbot/api dev:initdata`) подменяет окружение Telegram.
 */
const MOCK_FLAG = 'debtbot:mocked-env';

function mockForDev(): void {
  const initData = import.meta.env.VITE_DEV_INIT_DATA as string | undefined;
  if (!import.meta.env.DEV || !initData) return;
  // После первой подмены SDK хранит launch params в sessionStorage и isTMA() = true,
  // но mockTelegramEnv нужно вызывать на каждой загрузке страницы — иначе init() даёт UnknownEnvError.
  const wasMocked = sessionStorage.getItem(MOCK_FLAG) === '1';
  if (isTMA() && !wasMocked) return;
  const lp = new URLSearchParams({
    tgWebAppData: initData,
    tgWebAppVersion: '8.0',
    tgWebAppPlatform: 'web',
    tgWebAppThemeParams: JSON.stringify({
      bg_color: '#ffffff',
      text_color: '#000000',
      hint_color: '#999999',
      link_color: '#2481cc',
      button_color: '#2481cc',
      button_text_color: '#ffffff',
      secondary_bg_color: '#f0f0f0',
    }),
  });
  mockTelegramEnv({ launchParams: lp });
  sessionStorage.setItem(MOCK_FLAG, '1');
  mocked = true;
}

let mocked = false;
/** true, если окружение Telegram подменено для разработки/тестов (нет нативных кнопок). */
export function isMockedEnv(): boolean {
  return mocked;
}

function safe(fn: () => void): void {
  try {
    fn();
  } catch {
    // компонент недоступен на этой платформе/версии — это нормально
  }
}

/** Инициализация SDK. Возвращает null, если открыто не из Telegram. */
export function initTelegram(): TelegramEnv | null {
  try {
    mockForDev();
  } catch (err) {
    console.error('dev mock failed', err);
  }
  if (!isTMA()) return null;

  try {
    init();
  } catch (err) {
    console.error('telegram sdk init failed', err);
    return null;
  }

  safe(() => themeParams.mountSync());
  safe(() => themeParams.bindCssVars());
  safe(() => miniApp.mountSync());
  safe(() => miniApp.bindCssVars());
  safe(() => backButton.mount());
  safe(() => {
    void viewport.mount().then(() => {
      safe(() => viewport.bindCssVars());
      safe(() => viewport.expand());
    });
  });
  safe(() => miniApp.ready());

  const lp = retrieveLaunchParams();
  const rawInitData = retrieveRawInitData() ?? '';
  // web_app-кнопки бота не передают start_param — он приходит в query `?start=` (keyboards.ts)
  const startParamRaw =
    lp.tgWebAppStartParam ?? new URLSearchParams(window.location.search).get('start') ?? null;

  return {
    rawInitData,
    startParam: startParamRaw && START_PARAM_RE.test(startParamRaw) ? startParamRaw : null,
    platform: lp.tgWebAppPlatform,
    version: lp.tgWebAppVersion,
    languageCode: lp.tgWebAppData?.user?.language_code ?? null,
  };
}
